import * as React from 'react';
import { JsonFormsDispatch } from '@jsonforms/react';
import { StackLayout } from '@leagueplatform/genesis-core';
import type { AnnotatedJsonSchema } from '@web-config-app/core';
import type {
  ControlComponent,
  EntityFormControlProps,
} from '@web-config-app/entity-form';
import {
  useEntityFormControl,
  useParentSchema,
  useAnnotatedSchemaProps,
  useCombinatorSchemas,
  useEntityDetailsProps,
  getComposedSchemaPropertyPath,
} from '@web-config-app/entity-form';

type AnnotatedSchemaProps = ReturnType<typeof useAnnotatedSchemaProps>;
type CombinatorProperties = AnnotatedSchemaProps['combinatorProperties'];

const useParentSchemaCombinatorProperties = (path?: string) => {
  const [combinatorProps, setCombinatorProps] =
    React.useState<CombinatorProperties>();
  const { parentSchema } = useParentSchema();

  const { combinatorProperties } = useAnnotatedSchemaProps(
    parentSchema as AnnotatedJsonSchema,
    null,
    path,
  );

  if (combinatorProperties && !combinatorProps) {
    setCombinatorProps(combinatorProperties);
  }

  return { combinatorProperties: combinatorProps ?? combinatorProperties };
};

/**
 *
 * This component renders for schemas with `oneOf` where the the value for the
 * `discriminator`is pre-defined in the data for the property and the user does not have
 * the option to select a different value.
 *
 */

export const ConstantDiscriminatorCombinatorControlContents = ({
  path,
  rootSchema,
  data,
  visible,
  enabled,
  renderers,
  cells,
}: EntityFormControlProps) => {
  const { formPath } = useEntityDetailsProps();
  const { combinatorProperties } = useParentSchemaCombinatorProperties(
    getComposedSchemaPropertyPath(formPath, path),
  );

  const { combinatorDiscriminator } = combinatorProperties ?? {
    combinatorDiscriminator: '',
  };

  const { selectedSubSchema, detailUiSchema } = useCombinatorSchemas({
    combinatorProperties,
    rootSchema,
    data,
  });

  if (!selectedSubSchema) {
    // eslint-disable-next-line
    console.error(
      `Unable to find a schema matching discriminator ${combinatorDiscriminator} using discriminator value: ${
        (data ?? {})[combinatorDiscriminator]
      }`,
    );
  }

  if (!combinatorProperties) {
    // eslint-disable-next-line
    console.error(
      'Combinator properties are missing but required for the combinator',
    );
  }

  return combinatorProperties ? (
    <StackLayout spacing="$half" horizontalAlignment="stretch">
      {selectedSubSchema && (
        <JsonFormsDispatch
          visible={visible}
          enabled={enabled}
          schema={selectedSubSchema}
          uischema={detailUiSchema}
          path={path}
          renderers={renderers}
          cells={cells}
        />
      )}
    </StackLayout>
  ) : null;
};

export const ConstantDiscriminatorCombinatorControl: ControlComponent = (
  props,
) => {
  const entityFormControlProps = useEntityFormControl(props);

  return (
    <ConstantDiscriminatorCombinatorControlContents
      {...entityFormControlProps}
    />
  );
};
